import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnyAbility } from '@casl/ability';
import { AblePurePipe } from '@casl/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { CheckServiceActivitiesEditPermissionsService } from '@wilson/activities/pipes';
import { AuthState } from '@wilson/auth/core';
import { PartnershipsState } from '@wilson/clients/states';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import { FilterService } from '@wilson/filter';
import {
  Activity,
  AssignmentLayoutStyle,
  COMPACT_ROW_HEIGHT,
  COMPACT_SHIFT_HEIGHT,
  Filters,
  Label,
  MINIMAL_ROW_HEIGHT,
  MINIMAL_SHIFT_HEIGHT,
  OrganizationalUnit,
  PublicationStatus,
  RoleAction,
  RolePermissionSubject,
  Shift,
  ShiftCategory,
  ShiftColorOptions,
  ShiftDrawerCollapseDefaultSettings,
  ShiftSeriesShift,
  SortByOptions,
  SortDirection,
  StaffLayoutStyle,
  TimelineAssignmentLayoutQueryParamKey,
  TimelineTimeframe,
  UnassignedShift,
  User,
  UserTimelines,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { AbsenceDrawerHelperService } from '@wilson/non-domain-specific/absence';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import {
  SetPreferredSetting,
  SetShiftDrawerCollapseSettings,
  ShiftDrawerSettingsState,
  ShiftTimelinePreferredSettingsState,
} from '@wilson/preferred-settings/state';
import { SelectedItemOutput } from '@wilson/shift-timeline/assignments-gantt-chart';
import { TimelineHelperService } from '@wilson/shift-timeline/services';
import {
  FetchKpisForVisibleUsers,
  FetchProductivityForAllFilteredUsers,
  SetDefaultRowHeight,
  SetDefaultShiftHeight,
  SetDemarcatorRange,
  ShiftId,
  ShiftTimelineDataState,
  ShiftTimelineFilterStateService,
  ShiftTimelineSettingsState,
  ShiftTimelineUnassignedRegionState,
  AssignedRegionVisibilityStateService,
  TimelineSetDate,
  TimelineUpdateAssignmentsV2,
  UpdateAssignedShiftsKPIs,
  UpdateKpisOfUsers,
  UpdateProductivityOfUsers,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { isValid } from 'date-fns';
import { isEqual } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  forkJoin,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  catchError,
} from 'rxjs';
import {
  ShiftAccessRightConfirmationModalComponent,
  ShiftAccessRightConfirmationModalResult,
} from '../components-v2/shift-access-right-confirmation-modal/shift-access-right-confirmation-modal.component';
import {
  CloseAction,
  TimelineActivityDrawerService,
} from '../components/drawers/TimelineActivityDrawer/timeline-activity-drawer.service';
import { NUMBER_OF_DAYS_TO_LOAD } from '../components/shift-timeline-date-picker/constants';
import { DragAndDropHelperService } from './drag-and-drop-helper.service';

const SHIFT_CLIENT_COMMENTS_RELATIONS = [
  'shiftClientComments',
  'shiftClientComments.client',
];

@Injectable({
  providedIn: 'root',
})
export class TimelineCommonOperationsService {
  private readonly store = inject(Store);
  private readonly modalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private readonly absenceDrawerHelperService = inject(
    AbsenceDrawerHelperService,
  );
  private readonly timelineActivityDrawerService = inject(
    TimelineActivityDrawerService,
  );
  private readonly shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private readonly toastr = inject(NzMessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly filterService = inject(FilterService);
  private readonly shiftsServices = inject(ShiftsService);
  private readonly ablePurePipe =
    inject<AblePurePipe<AnyAbility>>(AblePurePipe);
  protected readonly timelineHelperService = inject(TimelineHelperService);
  private readonly assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );
  private readonly checkServiceActivitiesEditPermissionsService = inject(
    CheckServiceActivitiesEditPermissionsService,
  );
  private readonly dragAndDropHelperService = inject(DragAndDropHelperService);

  private updateScrollLeftPositionSubject = new BehaviorSubject<Date>(
    new Date(),
  );
  public updateScrollLeftPosition$ =
    this.updateScrollLeftPositionSubject.asObservable();

  private updatedScrollLeftPositionOnResizeSubject = new Subject<number>();
  public updatedScrollLeftPositionOnResize$ =
    this.updatedScrollLeftPositionOnResizeSubject.asObservable();

  @WilsonState(new Date())
  private refreshUnassignedRegionData!: Date;
  refreshUnassignedRegionData$!: Observable<Date>;

  refreshUnassignedRegion(): void {
    this.refreshUnassignedRegionData = new Date();
  }

  showSharedActivityUpdateRestrictionModal(): void {
    this.modalService.info({
      nzTitle: this.translate.instant(
        'page.shift_timeline.drawer.shift_form.edit_shift',
      ),
      nzContent: this.translate.instant(
        'page.shift_timeline.shift_drag_modal.contains_shared_activities',
      ),
      nzOkText: this.translate.instant('general.ok'),
      nzOkType: 'primary',
      nzOkDanger: false,
    });
  }

  openActivityDrawer({
    left,
    right,
    id,
  }: SelectedItemOutput): Observable<CloseAction | undefined> {
    this.store.dispatch(
      new SetDemarcatorRange([
        {
          left,
          right,
        },
      ]),
    );

    return this.timelineActivityDrawerService.openDrawer(id);
  }

  openAbsenceDrawer(
    left: number | null,
    right: number | null,
    absenceId: string,
  ): void {
    this.absenceDrawerHelperService.toggleAbsenceDrawer(true);
    this.absenceDrawerHelperService.setSelectedAbsenceId(absenceId);
    this.store.dispatch([
      new SetDemarcatorRange([
        {
          left,
          right,
        },
      ]),
    ]);
  }

  async checkAndConfirmPublishedShiftDrop(
    shiftPublicationStatus: PublicationStatus,
  ): Promise<boolean> {
    const isShiftPublished =
      shiftPublicationStatus === PublicationStatus.Published;

    if (isShiftPublished) {
      const modalRef = this.modalService.warning({
        nzTitle: this.translate.instant(
          'page.shift_timeline.drawer.shift_form.edit_shift',
        ),
        nzContent: this.translate.instant(
          'page.shift_timeline.published_shift_drag_modal.description',
        ),
        nzCancelText: this.translate.instant('general.cancel'),
        nzOkText: this.translate.instant('general.ok'),
        nzOkType: 'primary',
        nzOkDanger: false,
        nzOnOk: () => {
          modalRef.close(true);
        },
      });

      return await firstValueFrom(modalRef.afterClose);
    } else {
      return true;
    }
  }

  async checkAndConfirmDifferentOrganizationalUnit(
    shift: Shift,
    user: User & {
      accessibleOrganizationalUnits: OrganizationalUnit[];
    },
  ): Promise<ShiftAccessRightConfirmationModalResult | null> {
    const hasShiftOrganizationalUnitAccess =
      user.accessibleOrganizationalUnits.some(
        (accessibleOrganizationalUnit) =>
          accessibleOrganizationalUnit.id === shift.organizationalUnitId,
      );

    if (!hasShiftOrganizationalUnitAccess) {
      const modalRef = this.modalService.create({
        nzTitle: this.translate.instant(
          'page.shift_timeline.assign_shift.access_rights.title',
        ),
        nzContent: ShiftAccessRightConfirmationModalComponent,
        nzMaskClosable: false,
        nzKeyboard: false,
      });
      return await firstValueFrom(modalRef.afterClose);
    } else {
      return ShiftAccessRightConfirmationModalResult.ProceedAnyway;
    }
  }

  allowedToMoveShift(
    shift: Shift & {
      id: string;
      activities: Activity[];
      priority: number | null;
    },
  ): Observable<boolean> {
    const containsSharedActivities = shift.activities.some(
      (activity) => activity.jobId,
    );
    const canEditServiceActivities$ =
      this.checkServiceActivitiesEditPermissionsService.checkServiceActivitiesEditPermissions(
        shift.activities,
      );
    const partnersAllowActivityEditing$ = this.store
      .select(PartnershipsState.canEditActivities(shift.activities))
      .pipe(map((canEdit) => !!canEdit));

    return combineLatest([
      of(containsSharedActivities),
      canEditServiceActivities$,
      partnersAllowActivityEditing$,
    ]).pipe(
      take(1),
      map(
        ([
          containsSharedActivities,
          canEditServiceActivities,
          partnersAllowActivityEditing,
        ]) => {
          return containsSharedActivities
            ? partnersAllowActivityEditing && canEditServiceActivities
            : canEditServiceActivities;
        },
      ),
    );
  }

  getUserOvertimeAverageStream(): Observable<number> {
    return combineLatest([
      this.shiftTimelineFilterStateService.filteredUsers$,
      this.store.select(ShiftTimelineDataState.isLoadingUsersOvertimes),
      this.store.select(
        ShiftTimelineDataState.hasSuccessfullyFetchedUsersOvertimes,
      ),
    ]).pipe(
      map(
        ([
          filteredUsers,
          isLoadingUsersOvertimes,
          hasSuccessfullyFetchedUsersOvertimes,
        ]) => {
          if (
            !isLoadingUsersOvertimes &&
            hasSuccessfullyFetchedUsersOvertimes
          ) {
            const filteredUsersIds = filteredUsers.map(
              (filteredUser) => filteredUser.id as string,
            );
            let usersWithOvertimeCount = 0;
            const userOvertimeInMinutes = filteredUsersIds.map(
              (filteredUserId) => {
                const overtime = this.store.selectSnapshot(
                  ShiftTimelineDataState.userOvertimeMinutesStream(
                    filteredUserId,
                  ),
                );

                if (
                  typeof overtime === 'number' &&
                  (overtime < 0 || overtime > 0)
                ) {
                  usersWithOvertimeCount++;
                  return overtime;
                } else {
                  return 0;
                }
              },
            );

            const averageOvertimesInMinutes =
              userOvertimeInMinutes.reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0,
              ) / usersWithOvertimeCount;

            return averageOvertimesInMinutes;
          } else {
            return 0;
          }
        },
      ),
    );
  }

  public setPreferredSetting(): Observable<void> {
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (!userId) {
      return of(void 0);
    }

    const preferredSetting = this.store.selectSnapshot(
      ShiftTimelinePreferredSettingsState.getPreferredSetting(userId),
    );

    return combineLatest([
      this.featureFlagPurePipe.transform('portal-timeline-minimized-view'),
      this.featureFlagPurePipe.transform('portal-timeline-cell-view-mode'),
      from(
        this.getShiftColorOption(
          preferredSetting?.shiftColorOption ?? ShiftColorOptions.ShiftCategory,
        ),
      ),
    ]).pipe(
      map(
        ([
          isMinimalHeightFeatureFlagEnabled,
          isCellViewFeatureFlagEnabled,
          shiftColorOption,
        ]) => {
          const selectedStaffLayoutStyle =
            isMinimalHeightFeatureFlagEnabled && preferredSetting?.staffLayout
              ? preferredSetting.staffLayout
              : StaffLayoutStyle.Compact;

          const assignmentLayoutStyleFromQueryParams =
            this.route.snapshot.queryParamMap.get(
              TimelineAssignmentLayoutQueryParamKey,
            ) as AssignmentLayoutStyle | null;

          const isActualAssignmentLayoutStyle =
            assignmentLayoutStyleFromQueryParams ===
              AssignmentLayoutStyle.CellView ||
            assignmentLayoutStyleFromQueryParams ===
              AssignmentLayoutStyle.TimelineView;

          let selectedAssignmentLayoutStyle: AssignmentLayoutStyle;

          if (isCellViewFeatureFlagEnabled && isActualAssignmentLayoutStyle) {
            selectedAssignmentLayoutStyle =
              assignmentLayoutStyleFromQueryParams as AssignmentLayoutStyle;
          } else if (
            isCellViewFeatureFlagEnabled &&
            preferredSetting?.assignmentLayout
          ) {
            selectedAssignmentLayoutStyle = preferredSetting.assignmentLayout;
          } else {
            selectedAssignmentLayoutStyle = AssignmentLayoutStyle.TimelineView;
          }

          if (
            selectedAssignmentLayoutStyle !==
            assignmentLayoutStyleFromQueryParams
          ) {
            this.filterService.saveValueInQueryParam(
              TimelineAssignmentLayoutQueryParamKey,
              selectedAssignmentLayoutStyle,
            );
          }

          this.store.dispatch([
            new SetPreferredSetting(userId, {
              staffLayout: selectedStaffLayoutStyle,
              assignmentLayout: selectedAssignmentLayoutStyle,
              mainKPIs: preferredSetting?.mainKPIs ?? [],
              sortBy: preferredSetting?.sortBy ?? SortByOptions.Name,
              sortDirection:
                preferredSetting?.sortDirection ?? SortDirection.Ascending,
              threshold: preferredSetting?.threshold ?? {
                min: null,
                max: null,
              },
              shiftColorOption,
            }),
            new SetDefaultRowHeight(
              selectedStaffLayoutStyle === StaffLayoutStyle.Compact
                ? COMPACT_ROW_HEIGHT
                : MINIMAL_ROW_HEIGHT,
            ),
            new SetDefaultShiftHeight(
              selectedStaffLayoutStyle === StaffLayoutStyle.Compact
                ? COMPACT_SHIFT_HEIGHT
                : MINIMAL_SHIFT_HEIGHT,
            ),
          ]);
        },
      ),
    );
  }

  public setShiftDrawerCollapseSettings(): Observable<void> {
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (!userId) {
      return of(void 0);
    } else {
      return this.store
        .select(ShiftDrawerSettingsState.getShiftDrawerCollapseSettings(userId))
        .pipe(
          switchMap((shiftDrawerCollapseSettings) => {
            return this.store.dispatch(
              new SetShiftDrawerCollapseSettings(
                userId,
                shiftDrawerCollapseSettings ??
                  ShiftDrawerCollapseDefaultSettings,
              ),
            );
          }),
        );
    }
  }

  private getShiftColorOption(
    shiftColorOption: ShiftColorOptions,
  ): Observable<ShiftColorOptions> {
    return combineLatest([
      this.ablePurePipe.transform(RoleAction.Read, RolePermissionSubject.Label),
      this.ablePurePipe.transform(
        RoleAction.Read,
        RolePermissionSubject.Project,
      ),
    ]).pipe(
      map(([hasLabelReadAccess, hasProjectReadAccess]) => {
        if (
          (shiftColorOption === ShiftColorOptions.Label &&
            !hasLabelReadAccess) ||
          (shiftColorOption === ShiftColorOptions.Project &&
            !hasProjectReadAccess)
        ) {
          return ShiftColorOptions.ShiftCategory;
        }
        return shiftColorOption;
      }),
    );
  }

  public updateProductivityAndKpisOfUsers(userIds: string[]): void {
    if (this.isProductivityVisible()) {
      this.store.dispatch(new UpdateProductivityOfUsers(userIds));
    }
    if (this.isUserKpisVisible()) {
      this.store.dispatch(new UpdateKpisOfUsers(userIds));
    }
  }

  public async refreshTotalProductivityAllFilteredUsers(
    showLoading = false,
  ): Promise<void> {
    const filteredUsersIds = await this.getFilteredUsersIds();
    if (filteredUsersIds.length && this.isProductivityVisible()) {
      this.store.dispatch(
        new FetchProductivityForAllFilteredUsers(filteredUsersIds, showLoading),
      );
    }
  }

  public async refreshKpisForVisibleUsers(showLoading = false): Promise<void> {
    const visibleUserIdsSet = await firstValueFrom(
      this.assignedRegionVisibilityStateService.visibleUserIdsSet$,
    );
    const visibleUserIds = Array.from(visibleUserIdsSet);
    if (visibleUserIds.length && this.isUserKpisVisible()) {
      this.store.dispatch(
        new FetchKpisForVisibleUsers(visibleUserIds, showLoading),
      );
    }
  }

  private isProductivityVisible(): boolean {
    const userId = this.store.selectSnapshot(AuthState.userId);
    const isProductivityVisible = userId
      ? this.store.selectSnapshot(
          ShiftTimelinePreferredSettingsState.showProductivityKpi(userId),
        )
      : false;
    return isProductivityVisible;
  }

  private isUserKpisVisible(): boolean {
    const userId = this.store.selectSnapshot(AuthState.userId);
    return userId
      ? this.store.selectSnapshot(
          ShiftTimelinePreferredSettingsState.isUserKpisVisible(userId),
        )
      : false;
  }

  private async getFilteredUsersIds(): Promise<string[]> {
    const filteredUsers = await firstValueFrom(
      this.shiftTimelineFilterStateService.filteredUsers$,
    );

    const filteredUsersIds = filteredUsers.map(
      (filteredUser) => filteredUser.id as string,
    );
    return filteredUsersIds;
  }

  private extractUsersAndShiftIdsFromUserTimelines(
    usersWithShifts: UserTimelines[],
  ): {
    userId: string;
    shiftIds: ShiftId[];
  }[] {
    const usersAndShiftIds: {
      userId: string;
      shiftIds: ShiftId[];
    }[] = [];

    usersWithShifts.forEach((user) => {
      if (user.id) {
        usersAndShiftIds.push({
          userId: user.id,
          shiftIds: Object.keys(user.shiftsWithoutActivitiesDictionary),
        });
      }
    });
    return usersAndShiftIds;
  }

  public updateScrollLeftPositionOnResize(): void {
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );
    const scrollLeftPx = this.dragAndDropHelperService.calculatePxFromTime({
      time: new Date(visibleDateRange.start).toISOString(),
      snappingStepSize: 1,
      skipShadowShiftAllowance: true,
    });
    this.updatedScrollLeftPositionOnResizeSubject.next(scrollLeftPx);
  }

  public updateScrollLeftPosition(): void {
    this.updateScrollLeftPositionSubject.next(new Date());
  }

  public calculateNumberOfDaysToLoad(): number {
    const numberOfDays =
      this.store.selectSnapshot(
        ShiftTimelineSettingsState.numberOfDaysFromZoomLevel,
      ) + NUMBER_OF_DAYS_TO_LOAD;
    return numberOfDays;
  }

  public setTimelineDate(startDate: Date, endDate: Date): void {
    try {
      const dateRange = { start: startDate, end: endDate };
      this.store.dispatch(new TimelineSetDate(dateRange));

      this.updateScrollLeftPosition();
    } catch {
      this.toastr.error(this.translate.instant('general.something_went_wrong'));
    }
  }

  public updateAssignedShiftsKPIs(): void {
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );
    const currentUserId = this.store.selectSnapshot(AuthState.userId);
    if (
      currentUserId &&
      visibleDateRange.start &&
      visibleDateRange.end &&
      isValid(visibleDateRange.start) &&
      isValid(visibleDateRange.end)
    ) {
      const isCellView = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.isCellView(currentUserId),
      );
      this.store.dispatch(
        new UpdateAssignedShiftsKPIs(visibleDateRange, isCellView),
      );
    }
  }

  public getShiftFromUnassignedRegion(
    shiftId: string,
  ): UnassignedShift | undefined {
    const currentUserId = this.store.selectSnapshot(AuthState.userId);
    let data = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.getShiftWithoutActivities(shiftId),
    );

    if (currentUserId) {
      const isCellView = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.isCellView(currentUserId),
      );
      if (isCellView) {
        data = this.store.selectSnapshot(
          ShiftTimelineUnassignedRegionState.getCellViewShiftWithoutActivities(
            shiftId,
          ),
        );
      }
    }

    return data;
  }

  public fetchShiftWithTranslatedActivityCategoryAndSortActivities(
    selectedShiftId: string,
    options?: { includeClientComments?: boolean },
  ): Observable<Shift | null> {
    return this.shiftsServices
      .fetchShiftWithTranslatedActivityCategoryAndSortActivities(
        selectedShiftId,
        {
          relations: [
            `user`,
            `user.contract`,
            `user.contract.activityCategoryContracts`,
            `shiftSeries`,
            `shiftCategory`,
            `organizationalUnit`,
            `activities`,
            `activities.agreement`,
            `activities.profession`,
            `activities.agreement.client`,
            `activities.endLocation`,
            `activities.startLocation`,
            `activities.activityCategory`,
            `activities.activityReports`,
            `activities.serviceDeviations`,
            `activities.service`,
            `labels`,
            `payrollInformation`,
            `startLocation`,
            `endLocation`,
            ...(options?.includeClientComments
              ? SHIFT_CLIENT_COMMENTS_RELATIONS
              : []),
          ],
        },
      )
      .pipe(
        catchError((error) => {
          console.error(
            `Error fetching or processing shift data for ID ${selectedShiftId}:`,
            error,
          );
          this.toastr.error(
            this.translate.instant('general.something_went_wrong'),
          );
          return of(null);
        }),
      );
  }

  public fetchShift(unassignedShiftIds: string[]): Observable<
    (Shift &
      WithPreparedAttributes & {
        id: string;
        organizationalUnit: OrganizationalUnit | null;
        user: User | null;
        shiftCategory: ShiftCategory | null;
        shiftSeries: ShiftSeriesShift | null;
        labels: Label[];
      })[]
  > {
    const shiftObservables = unassignedShiftIds.map((id) =>
      this.shiftsServices.get<
        Shift &
          WithPreparedAttributes & {
            id: string;
            organizationalUnit: OrganizationalUnit | null;
            user: User | null;
            shiftCategory: ShiftCategory | null;
            shiftSeries: ShiftSeriesShift | null;
            labels: Label[];
          }
      >(id, {
        relations: [
          'organizationalUnit',
          'user',
          'shiftCategory',
          'shiftSeries',
          'labels',
          'startLocation',
          'endLocation',
        ],
      }),
    );

    return forkJoin(shiftObservables);
  }

  public areDateRangesAndFiltersSame(
    previous: [{ start: number; end: number }, Filters],
    current: [{ start: number; end: number }, Filters],
  ): boolean {
    const [previousVisibleDateRange, previousFilters] = previous;
    const [newVisibleDateRange, newFilters] = current;

    const isStartDateSame =
      previousVisibleDateRange.start &&
      newVisibleDateRange.start &&
      previousVisibleDateRange.start === newVisibleDateRange.start;

    const isEndDateSame =
      previousVisibleDateRange.end &&
      newVisibleDateRange.end &&
      previousVisibleDateRange.end === newVisibleDateRange.end;

    const areFiltersSame = isEqual(previousFilters, newFilters);

    return !!(isStartDateSame && isEndDateSame && areFiltersSame);
  }

  public createUnassignedRegionDataObservable(): Observable<TimelineTimeframe> {
    return combineLatest([
      this.store.select(ShiftTimelineDataState.visibleDateRange),
      this.shiftTimelineFilterStateService.filters$,
      this.refreshUnassignedRegionData$,
    ]).pipe(
      debounceTime(50),
      distinctUntilChanged(
        (
          [
            previousVisibleDateRange,
            previousFilters,
            previousRefreshUnassignedRegionData,
          ],
          [newVisibleDateRange, newFilters, nextRefreshUnassignedRegionData],
        ) => {
          const isDateRangesAndFiltersSame = this.areDateRangesAndFiltersSame(
            [previousVisibleDateRange, previousFilters],
            [newVisibleDateRange, newFilters],
          );

          const isUnassignedRegionExpandedSame =
            previousRefreshUnassignedRegionData.toString() ===
            nextRefreshUnassignedRegionData.toString();

          return isDateRangesAndFiltersSame && isUnassignedRegionExpandedSame;
        },
      ),
      map(([visibleDateRange]) => {
        return {
          start: visibleDateRange.start,
          end: visibleDateRange.end,
        };
      }),
    );
  }

  public updateUsersAssignmentsState(userIds: string[]): Observable<void> {
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );
    return this.store.dispatch(
      new TimelineUpdateAssignmentsV2({
        userIds,
        dates: visibleDateRange,
      }),
    );
  }
}
